/**
 * Results XLSX Generator Utility
 * Generates downloadable XLSX file with original data plus workflow results
 */
import * as XLSX from "xlsx";
import { buildHeaderToFieldMap } from "./columnMapper";
/** Workflow result columns appended after the original input columns */
const RESULT_COLUMNS = [
    { header: "Created On", field: "createdOn" },
    { header: "V5UserId", field: "v5UserId" },
    { header: "IdentityId", field: "identityId" },
    { header: "Errors", field: "errors" },
];
/**
 * Generate an XLSX file with all original data plus workflow results.
 * Uses the original headers from the uploaded file, preserving column names and order.
 * Appends workflow result columns (Created On, V5UserId, IdentityId, Errors) at the end.
 * @param records - User records with populated workflow result fields
 * @param filename - Optional filename
 */
export function downloadResultsXlsx(records, filename) {
    const blob = generateResultsBlob(records);
    const finalFilename = filename || `UserProvisioning_Results_${generateTimestamp()}.xlsx`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = finalFilename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
/**
 * Generate XLSX blob with all data.
 * If the original workbook is available, clones it and appends result columns in-place,
 * preserving all original formatting, styles, column widths, and sheet structure.
 * Falls back to building a new workbook from scratch if no original workbook is present.
 * @param records - User records with populated workflow result fields
 */
export function generateResultsBlob(records) {
    if (records.length > 0 && records[0].originalFileBuffer) {
        return generateResultsBlobFromOriginal(records);
    }
    return generateResultsBlobFromScratch(records);
}
/**
 * Re-read the original file bytes and append result columns to the first sheet.
 * Re-reading from the raw ArrayBuffer preserves ALL original formatting, styles,
 * themes, column widths, and sheet structure — nothing is lost.
 */
function generateResultsBlobFromOriginal(records) {
    // Re-read from the original file bytes — this is a true faithful clone
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const workbook = XLSX.read(records[0].originalFileBuffer, {
        type: "array",
        cellStyles: true,
    });
    const sheetName = workbook.SheetNames[0];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sheet = workbook.Sheets[sheetName];
    // Parse the sheet's existing range
    const ref = sheet["!ref"];
    const range = ref ? XLSX.utils.decode_range(ref) : { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } };
    // Determine which result columns are not already present in the header row
    const existingHeaders = new Set();
    for (let c = range.s.c; c <= range.e.c; c++) {
        const cellAddr = XLSX.utils.encode_cell({ r: 0, c });
        const cell = sheet[cellAddr];
        if (cell && cell.v !== undefined) {
            existingHeaders.add(String(cell.v));
        }
    }
    const resultColumnsToAppend = RESULT_COLUMNS.filter((col) => !existingHeaders.has(col.header));
    if (resultColumnsToAppend.length === 0) {
        // All result columns already exist — just update data rows with result values
        updateExistingResultColumns(sheet, range, records);
    }
    else {
        // Append new result columns after the last existing column
        let nextCol = range.e.c + 1;
        for (const col of resultColumnsToAppend) {
            // Write header cell (row 0)
            const headerAddr = XLSX.utils.encode_cell({ r: 0, c: nextCol });
            sheet[headerAddr] = { t: "s", v: col.header };
            // Write data cells (one per record, starting at row 1)
            for (let rowIdx = 0; rowIdx < records.length; rowIdx++) {
                const record = records[rowIdx];
                const value = record[col.field];
                const cellAddr = XLSX.utils.encode_cell({ r: rowIdx + 1, c: nextCol });
                if (value !== null && value !== undefined && value !== "") {
                    sheet[cellAddr] = { t: "s", v: String(value) };
                }
            }
            // Add column width for the new column
            if (!sheet["!cols"])
                sheet["!cols"] = [];
            sheet["!cols"][nextCol] = { wch: Math.max(col.header.length + 2, 15) };
            nextCol++;
        }
        // Expand the sheet range to include the new columns
        range.e.c = nextCol - 1;
        range.e.r = Math.max(range.e.r, records.length);
        sheet["!ref"] = XLSX.utils.encode_range(range);
    }
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array", cellStyles: true });
    return new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
}
/**
 * Update data in result columns that already exist in the sheet.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function updateExistingResultColumns(sheet, range, records) {
    // Build a map of header name -> column index
    const headerColMap = new Map();
    for (let c = range.s.c; c <= range.e.c; c++) {
        const cellAddr = XLSX.utils.encode_cell({ r: 0, c });
        const cell = sheet[cellAddr];
        if (cell && cell.v !== undefined) {
            headerColMap.set(String(cell.v), c);
        }
    }
    for (const col of RESULT_COLUMNS) {
        const colIdx = headerColMap.get(col.header);
        if (colIdx === undefined)
            continue;
        for (let rowIdx = 0; rowIdx < records.length; rowIdx++) {
            const record = records[rowIdx];
            const value = record[col.field];
            const cellAddr = XLSX.utils.encode_cell({ r: rowIdx + 1, c: colIdx });
            if (value !== null && value !== undefined && value !== "") {
                sheet[cellAddr] = { t: "s", v: String(value) };
            }
        }
    }
}
/**
 * Fallback: build a new workbook from scratch when no original workbook is available.
 */
function generateResultsBlobFromScratch(records) {
    const workbook = XLSX.utils.book_new();
    const originalHeaders = records.length > 0 ? records[0].originalHeaders : [];
    const headerToField = buildHeaderToFieldMap();
    const originalHeaderSet = new Set(originalHeaders);
    const resultColumnsToAppend = RESULT_COLUMNS.filter((col) => !originalHeaderSet.has(col.header));
    const outputHeaders = [...originalHeaders, ...resultColumnsToAppend.map((c) => c.header)];
    const dataRows = records.map((record) => {
        const row = [];
        for (const header of originalHeaders) {
            const fieldName = headerToField[header];
            if (fieldName) {
                const value = record[fieldName];
                if (value === null || value === undefined || fieldName === "validationErrors") {
                    row.push("");
                }
                else {
                    row.push(value);
                }
            }
            else {
                const extra = record.extraColumns[header];
                row.push(extra ?? "");
            }
        }
        for (const col of resultColumnsToAppend) {
            const value = record[col.field];
            row.push(value !== null && value !== undefined ? value : "");
        }
        return row;
    });
    const wsData = [outputHeaders, ...dataRows];
    const worksheet = XLSX.utils.aoa_to_sheet(wsData);
    const colWidths = outputHeaders.map((h) => ({ wch: Math.max(h.length + 2, 15) }));
    worksheet["!cols"] = colWidths;
    XLSX.utils.book_append_sheet(workbook, worksheet, "User Provisioning Results");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    return new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
}
/**
 * Generate a timestamped string for filenames
 */
function generateTimestamp() {
    return new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
}
/**
 * Merge created user data into user records
 * @param records - Original user records
 * @param createdUsers - Created users from provisioning job
 * @param completionTimestamp - Timestamp when provisioning completed
 */
export function mergeWorkflowResults(records, createdUsers, completionTimestamp) {
    const timestamp = completionTimestamp || new Date().toISOString();
    // Create a copy of records to avoid mutation
    const updatedRecords = records.map((record) => ({ ...record }));
    // Match created users to records
    for (const createdUser of createdUsers) {
        // Try to find matching record by display name or email
        const matchingRecord = updatedRecords.find((record) => {
            const recordDisplayName = `${record.lastName || ""}, ${record.firstName || ""}`.trim();
            const recordDisplayNameWithCredential = `${recordDisplayName}${record.credentials ? `, ${record.credentials}` : ""}`.trim();
            // Match by display name (with or without credentials)
            if (createdUser.displayName === recordDisplayName ||
                createdUser.displayName === recordDisplayNameWithCredential) {
                return true;
            }
            // Match by email if available
            if (record.email && createdUser.displayName.includes(record.email)) {
                return true;
            }
            return false;
        });
        if (matchingRecord) {
            // Populate workflow result fields
            matchingRecord.createdOn = timestamp;
            matchingRecord.v5UserId = createdUser.userId || null;
            matchingRecord.identityId = createdUser.identityId || null;
            // Concatenate error and warning messages
            const errorMessages = createdUser.messages
                ?.filter((m) => m.type === "Error" || m.type === "Warn")
                .map((m) => `[${m.type}] ${m.message}`)
                .join(" | ");
            matchingRecord.errors = errorMessages || null;
        }
    }
    // For records with validation errors that weren't provisioned, populate errors field
    for (const record of updatedRecords) {
        if (record.validationErrors && record.validationErrors.length > 0 && !record.v5UserId) {
            const validationErrorMessages = record.validationErrors
                .map((e) => `${e.fieldName}: ${e.errorMessage}`)
                .join(" | ");
            record.errors = validationErrorMessages;
        }
    }
    return updatedRecords;
}
